import { useState, useEffect } from 'react';
import { api, API_BASE } from '../../utils/api';
import { printViaAgent } from '../../utils/printAgent';
import { nowEatDateString, nowEatTimeString } from '../../utils/datetime';
import { Button, Spinner } from '../shared/UI';
const STEP = { IDLE: 'idle', INFO: 'info', SERVICES: 'services', CONFIRM: 'confirm', DONE: 'done' };

function IdleScreen({ onStart, orgName, logoPath, fontSize }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 40, cursor: 'pointer' }} onClick={onStart}>
      <div style={{ textAlign: 'center', animation: 'fadeIn 0.6s ease', padding: '0 40px' }}>
        <div style={{ marginBottom: 32, animation: 'pulse 2s ease infinite' }}>
          {logoPath ? (
            <img src={`${API_BASE}${logoPath}`} alt="Logo" style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain' }} />
          ) : (
             <div style={{ fontSize: 100 }}>🎫</div>
          )}
        </div>
        <h1 
          style={{ fontFamily: 'inherit', fontWeight: 800, fontSize: fontSize || 52, letterSpacing: '-0.03em', textShadow: '0 4px 30px rgba(0,0,0,0.3)', textTransform: 'uppercase' }}
          dangerouslySetInnerHTML={{ __html: orgName }}
        />
        <p style={{ fontSize: 22, opacity: 0.75, marginTop: 12, fontWeight: 300 }}>Touch anywhere to get started</p>
      </div>
      <div style={{ padding: '16px 40px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 50, border: '1.5px solid rgba(255,255,255,0.3)', fontSize: 16, fontFamily: 'inherit', fontWeight: 600, animation: 'pulse 2s ease infinite' }}>
        Tap to begin
      </div>
    </div>
  );
}

function InfoForm({ onNext, onBack }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+254');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Full name is required';
    if (!phone.trim()) e.phone = 'Phone number is required';
    else if (phone.replace(/\D/g, '').length < 9) e.phone = 'Enter a valid number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div className="kiosk-padding" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '40px 60px', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ fontFamily: 'inherit', fontWeight: 800, fontSize: 38 }}>Your Details</h2>
        <p style={{ opacity: 0.7, fontSize: 18, marginTop: 8 }}>Please enter your information to proceed</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, flex: 1 }}>
        <div>
          <label style={{ display: 'block', opacity: 0.8, fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Full Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your full name"
            style={{ width: '100%', padding: '18px 22px', fontSize: 20, borderRadius: 14, border: errors.name ? '2px solid var(--red)' : '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: 'inherit', outline: 'none', backdropFilter: 'blur(4px)' }} />
          {errors.name && <p style={{ color: 'var(--red)', marginTop: 6, fontSize: 14 }}>{errors.name}</p>}
        </div>
        <div>
          <label style={{ display: 'block', opacity: 0.8, fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Phone Number *</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <select value={countryCode} onChange={e => setCountryCode(e.target.value)}
              style={{ padding: '18px', fontSize: 20, borderRadius: 14, border: '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.2)', color: '#000', outline: 'none', cursor: 'pointer' }}>
              <option value="+254">🇰🇪 +254</option>
              <option value="+255">🇹🇿 +255</option>
              <option value="+256">🇺🇬 +256</option>
              <option value="+1">🇺🇸 +1</option>
              <option value="+44">🇬🇧 +44</option>
            </select>
            <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="7XXXXXXXX" type="tel"
              style={{ flex: 1, padding: '18px 22px', fontSize: 20, borderRadius: 14, border: errors.phone ? '2px solid var(--red)' : '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: 'inherit', outline: 'none', backdropFilter: 'blur(4px)' }} />
          </div>
          {errors.phone && <p style={{ color: 'var(--red)', marginTop: 6, fontSize: 14 }}>{errors.phone}</p>}
          <p style={{ opacity: 0.5, fontSize: 13, marginTop: 6 }}>🔒 Your phone number will be masked on your printed ticket</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
        <button onClick={onBack} style={{ padding: '18px 32px', fontSize: 18, borderRadius: 14, border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
        <button onClick={() => validate() && onNext({ name: name.trim(), phone: `${countryCode} ${phone.trim()}` })}
          style={{ flex: 1, padding: '18px 32px', fontSize: 18, borderRadius: 14, border: 'none', background: '#fff', color: '#000', cursor: 'pointer', fontWeight: 800 }}>
          Continue →
        </button>
      </div>
    </div>
  );
}

function ServiceGrid({ services, onSelect, title, onBack, parentIcon }) {
  return (
    <div className="kiosk-padding" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '36px 48px', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', gap: 20 }}>
        {onBack && <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'inherit', width: 44, height: 44, borderRadius: 12, fontSize: 20, cursor: 'pointer' }}>←</button>}
        <div>
          <h2 style={{ fontWeight: 800, fontSize: 34 }}>{title}</h2>
          <p style={{ opacity: 0.6, fontSize: 16, marginTop: 4 }}>Select a service</p>
        </div>
      </div>
      <div className="kiosk-service-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 160px)', gap: 16, flex: 1, overflowY: 'auto', justifyContent: 'center', alignContent: 'start', padding: '20px 0' }}>
        {services.map(svc => {
          const icon = svc.icon_path || parentIcon;
          const unavailable = svc.is_leaf && svc.is_available === false;
          return (
            <button key={svc.id} 
              onClick={unavailable ? null : () => onSelect(svc)}
              disabled={unavailable}
              className="kiosk-service-item"
              style={{
                width: 160, height: 160, padding: 12, borderRadius: 16, border: '1px solid rgba(0,0,0,0.08)',
                background: unavailable ? '#e0e0e0' : '#f5f5f5', 
                color: '#000000', cursor: unavailable ? 'not-allowed' : 'pointer', 
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                transition: 'all 0.18s',
                opacity: unavailable ? 0.6 : 1,
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={e => { if(!unavailable) { e.currentTarget.style.background = '#eeeeee'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.1)'; } }}
              onMouseLeave={e => { if(!unavailable) { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; } }}>
              {icon ? (
                <img src={icon.startsWith('http') ? icon : `${API_BASE}${icon}`} alt={svc.name} style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#000' }}>
                  {svc.ticket_prefix || svc.name.slice(0, 2)}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 700, textAlign: 'center', lineHeight: 1.2, color: '#000000' }}>{svc.name}</span>
                {unavailable && <span style={{ fontSize: 9, fontWeight: 800, color: '#d32f2f', textTransform: 'uppercase' }}>Unavailable</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConfirmScreen({ service, customerInfo, onConfirm, onBack, loading }) {
  return (
    <div className="kiosk-padding" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px 60px', animation: 'fadeIn 0.3s ease' }}>
      <h2 style={{ fontWeight: 800, fontSize: 36, marginBottom: 32 }}>Confirm Selection</h2>
      <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', borderRadius: 24, padding: '36px 48px', width: '100%', maxWidth: 480, border: '1px solid rgba(255,255,255,0.2)', marginBottom: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Row label="Service" value={service?.name} />
          {customerInfo?.name && <Row label="Name" value={customerInfo.name} />}
          {customerInfo?.phone && <Row label="Phone" value={customerInfo.phone} />}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, width: '100%', maxWidth: 480 }}>
        <button onClick={onBack} style={{ flex: 1, padding: '18px', fontSize: 18, borderRadius: 14, border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
        <button onClick={onConfirm} disabled={loading}
          style={{ flex: 2, padding: '18px', fontSize: 20, borderRadius: 14, border: 'none', background: '#fff', color: '#000', cursor: 'pointer', fontWeight: 800 }}>
          {loading ? 'Issuing…' : '🎫 Get My Ticket'}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 14 }}>
      <span style={{ opacity: 0.6, fontSize: 15 }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function ticketCounterText(ticket) {
  return ticket?.counter_display || ticket?.counter_label || ticket?.counter_name || '';
}

function applyTicketTemplate(template, { ticket, orgName, html = false }) {
  const dateStr = nowEatDateString({ day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = nowEatTimeString({ hour: '2-digit', minute: '2-digit' });
  const org = orgName || 'QMS';
  const ticketNo = ticket?.ticket_number || '';
  const service = ticket?.service_name || '';
  const customer = ticket?.customer_name || 'Valued Customer';
  const counter = ticketCounterText(ticket);
  const phone = ticket?.masked_phone || ticket?.phone_number || '';
  const dateTime = `${dateStr} ${timeStr}`;

  if (html) {
    return template
      .replace(/\{org_name\}/gi, `<span style="font-size:18px;font-weight:900;">${org}</span>`)
      .replace(/\{ticket\}/gi, `<span style="font-size:58px;font-weight:900;letter-spacing:0.08em;">${ticketNo}</span>`)
      .replace(/\{service\}/gi, `<span style="font-weight:600;">${service}</span>`)
      .replace(/\{customer\}/gi, `<span>${customer}</span>`)
      .replace(/\{counter\}/gi, `<span style="font-weight:600;">${counter}</span>`)
      .replace(/\{phone\}/gi, `<span>${phone}</span>`)
      .replace(/\{date\}/gi, `<span>${dateTime}</span>`)
      .replace(/\n/g, '<br/>');
  }

  return template
    .replace(/\{org_name\}/gi, org)
    .replace(/\{ticket\}/gi, ticketNo)
    .replace(/\{service\}/gi, service)
    .replace(/\{customer\}/gi, customer)
    .replace(/\{counter\}/gi, counter)
    .replace(/\{phone\}/gi, phone)
    .replace(/\{date\}/gi, dateTime);
}

function DoneScreen({ ticket, orgName, ticketTemplate, onReset }) {
  const [printStatus, setPrintStatus] = useState('printing');

  useEffect(() => {
    let cancelled = false;

    const runPrint = async () => {
      try {
        await printViaAgent({ ticket, orgName, ticketTemplate });
        if (!cancelled) setPrintStatus('printed');
      } catch (err) {
        console.warn('[Kiosk] ESC/POS print agent unavailable, using browser print:', err);
        if (!cancelled) {
          setPrintStatus('fallback');
          window.print();
        }
      }
    };

    const printTimer = setTimeout(runPrint, 400);
    const t = setTimeout(onReset, 15000);
    return () => {
      cancelled = true;
      clearTimeout(printTimer);
      clearTimeout(t);
    };
  }, [ticket, orgName, ticketTemplate]);

  const buildReceiptHtml = () => {
    const dateStr = nowEatDateString({ day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = nowEatTimeString({ hour: '2-digit', minute: '2-digit' });
    const counter = ticketCounterText(ticket);

    if (ticketTemplate) {
      return applyTicketTemplate(ticketTemplate, { ticket, orgName, html: true });
    }

    const orgBlock = `<div style="font-size:18px;font-weight:900;letter-spacing:0.02em;margin-bottom:6px;">${orgName || 'QMS'}</div>`;
    const ticketBlock = `<div style="font-size:58px;font-weight:900;letter-spacing:0.08em;line-height:1;margin:10px 0;">${ticket?.ticket_number || ''}</div>`;

    return [
      orgBlock,
      `<div style="font-size:11px;border-bottom:1px solid #000;padding-bottom:6px;margin-bottom:8px;">Queue Management System</div>`,
      `<div style="font-size:11px;margin-bottom:4px;">Service: <strong>${ticket?.service_name || ''}</strong></div>`,
      `<div style="font-size:10px;margin-bottom:12px;">${dateStr} &nbsp;|&nbsp; ${timeStr}</div>`,
      `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:2px;">Your Ticket Number</div>`,
      ticketBlock,
      ticket?.customer_name ? `<div style="font-size:11px;margin-top:6px;">Name: ${ticket.customer_name}</div>` : '',
      counter ? `<div style="font-size:11px;margin-top:2px;">Counter: <strong>${counter}</strong></div>` : '',
      `<div style="font-size:11px;margin-top:12px;font-weight:600;">Please wait for your turn</div>`,
    ].join('');
  };

  return (
    <>
      {/* Receipt — hidden on screen, visible only on @media print (see index.css) */}
      {/* Browser print fallback only (hidden on screen; no cut line — physical cut is ESC/POS) */}
      <div id="print-section">
        <div dangerouslySetInnerHTML={{ __html: buildReceiptHtml() }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 28, animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
        <div style={{ fontSize: 70 }}>✅</div>
        <h2 style={{ fontWeight: 800, fontSize: 32, textAlign: 'center' }}>Ticket Issued!</h2>
        <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 24, padding: '32px 56px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, opacity: 0.7, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Your Ticket Number</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 64, letterSpacing: '0.08em', textShadow: '0 0 30px rgba(255,255,255,0.3)' }}>{ticket?.ticket_number}</div>
          <div style={{ marginTop: 12, fontSize: 15, opacity: 0.7 }}>Counter: <strong>{ticketCounterText(ticket)}</strong></div>
        </div>
        <p style={{ opacity: 0.6, fontSize: 15 }}>
          {printStatus === 'printed'
            ? 'Ticket printed. Screen resets in 15s.'
            : printStatus === 'fallback'
              ? 'Printing your ticket... Screen resets in 15s.'
              : 'Sending to printer...'}
        </p>
        <button onClick={onReset} style={{ padding: '14px 32px', borderRadius: 50, border: '1.5px solid rgba(255,255,255,0.4)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
          Start Over
        </button>
      </div>
    </>
  );
}

export default function KioskApp() {
  const [step, setStep] = useState(STEP.IDLE);
  const [kioskConfig, setKioskConfig] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [ticketTemplate, setTicketTemplate] = useState('');
  const [allServices, setAllServices] = useState([]);
  const [serviceStack, setServiceStack] = useState([]); 
  const [currentLevelServices, setCurrentLevelServices] = useState([]);
  const [selectedLeaf, setSelectedLeaf] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [issuedTicket, setIssuedTicket] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const [styles, setStyles] = useState({
    bg: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 50%, var(--cobalt) 100%)',
    text: '#ffffff',
    welcome: '',
    ticker: '',
    logo: '',
    fontSize: '52px',
    fontFamily: "'Outfit', sans-serif"
  });

  const fetchData = async () => {
    try {
      const cfg = await api.get('/api/admin/config');
      setOrgName(cfg.org_name || 'Queue Management System');
      setTicketTemplate(cfg.ticket_template || '');
      setKioskConfig({ data_collection_enabled: cfg.data_collection_enabled === 'true' });
      setStyles({
        bg: cfg.kiosk_bg_color || 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 50%, var(--cobalt) 100%)',
        text: cfg.kiosk_text_color || '#ffffff',
        welcome: cfg.kiosk_welcome_text || '',
        ticker: cfg.kiosk_ticker_message || '',
        logo: cfg.kiosk_logo_path || '',
        fontSize: cfg.kiosk_font_size || '52px',
        fontFamily: cfg.kiosk_font_family || "'Outfit', sans-serif"
      });

      const data = await api.get('/api/services/?tree=true&active=true');
      setAllServices(data);
      if (step === STEP.IDLE || step === STEP.SERVICES) {
        // Only update current level if not in sub-menu
        if (serviceStack.length === 0) setCurrentLevelServices(data);
      }
    } catch (err) { console.error("Silent refresh failed", err); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Fast refresh every 5s for real-time availability
    return () => clearInterval(interval);
  }, []);

  const reset = () => {
    setStep(STEP.IDLE);
    setServiceStack([]);
    setCurrentLevelServices(allServices);
    setSelectedLeaf(null);
    setCustomerInfo(null);
    setIssuedTicket(null);
  };

  const start = () => {
    if (kioskConfig?.data_collection_enabled) setStep(STEP.INFO);
    else setStep(STEP.SERVICES);
  };

  const handleServiceSelect = (svc) => {
    if (svc.children?.length > 0) {
      setServiceStack([...serviceStack, currentLevelServices]);
      setCurrentLevelServices(svc.children);
    } else {
      setSelectedLeaf(svc);
      setStep(STEP.CONFIRM);
    }
  };

  const handleBackLevel = () => {
    const prev = serviceStack.pop();
    setServiceStack([...serviceStack]);
    setCurrentLevelServices(prev);
  };

  const issueTicket = async () => {
    setIssuing(true);
    const body = {
      service_id: selectedLeaf?.id,
      customer_name: customerInfo?.name || null,
      phone_number: customerInfo?.phone || null,
    };
    try {
      const ticket = await api.post('/api/tickets/issue', body);
      setIssuedTicket(ticket);
      setStep(STEP.DONE);
    } catch (err) {
      console.error('Ticket issue error:', err);
      // Show the most descriptive error possible
      const msg = err.message || err.error || err.detail || JSON.stringify(err);
      alert('Error: ' + msg);
    } finally {
      setIssuing(false);
    }
  };

  const finalWelcome = styles.welcome.replace('{org_name}', orgName) || orgName;

  return (
    <div style={{ 
      width: '100vw', height: '100vh', 
      background: styles.bg, color: styles.text, 
      fontFamily: styles.fontFamily,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' 
    }}>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
      <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none' }} />

      <div style={{ padding: '18px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em' }}>
          {orgName} <span style={{ fontWeight: 400, opacity: 0.5 }}>· Shop</span>
        </div>
        <div style={{ fontSize: 13, opacity: 0.6 }}>
          {nowEatTimeString()}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        {!kioskConfig ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><Spinner /></div> :
          step === STEP.IDLE ? <IdleScreen onStart={start} orgName={finalWelcome} logoPath={styles.logo} fontSize={styles.fontSize} /> :
          step === STEP.INFO ? <InfoForm onNext={info => { setCustomerInfo(info); setStep(STEP.SERVICES); }} onBack={reset} /> :
          step === STEP.SERVICES ? (
            <ServiceGrid 
              services={currentLevelServices} 
              onSelect={handleServiceSelect} 
              title={serviceStack.length > 0 ? "Select Sub-service" : "Select a Service"} 
              onBack={serviceStack.length > 0 ? handleBackLevel : (kioskConfig?.data_collection_enabled ? () => setStep(STEP.INFO) : reset)} 
            />
          ) :
          step === STEP.CONFIRM ? <ConfirmScreen service={selectedLeaf} customerInfo={customerInfo} onConfirm={issueTicket} onBack={() => setStep(STEP.SERVICES)} loading={issuing} /> :
          step === STEP.DONE ? <DoneScreen ticket={issuedTicket} orgName={orgName} ticketTemplate={ticketTemplate} onReset={reset} /> : null
        }
      </div>

      {styles.ticker && (
        <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 0', overflow: 'hidden', whiteSpace: 'nowrap', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'inline-block', animation: 'marquee 25s linear infinite', paddingLeft: '100%', fontSize: 14, fontWeight: 500, letterSpacing: '0.02em' }}>
            {styles.ticker} &nbsp;&nbsp;&nbsp;&nbsp; • &nbsp;&nbsp;&nbsp;&nbsp; {styles.ticker}
          </div>
        </div>
      )}
    </div>
  );
}
